import { type HomeyAPI } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { BaseApiStore, FetchArgs } from '../../../store/BaseApiStore.ts';

import { wait } from '../../../lib/wait';
import { formatBytes } from '../../../lib/formatBytes';

import { useAttach } from '../../../store/useAttach';
import { selectBase } from '../../../store/selectors.ts';

type MemoryInfoItem = { name: string; size: number };
type MemoryInfo = {
  free: number;
  total: number;
  swap?: number;
  types: { [key: string]: MemoryInfoItem };
};

interface State {
  memoryInfoData: object | null;
  free: number;
  freeFormatted: string;
  total: number;
  totalFormatted: string;
  swap: number | null;
  swapFormatted: string | null;
  items: (MemoryInfoItem & { key: string; sizeFormatted: string; color: string })[];
  isLoading: boolean;
  error: unknown;
}

type GeneralSettingsInfoMemoryStoreState = State;

function getColorForIndex(index: number) {
  let hue = index * 50;
  hue += 150;
  hue %= 360;

  return `hsl(${hue}, 80%, 70%)`;
}

class GeneralSettingsInfoMemoryStore extends BaseApiStore<State> {
  constructor() {
    super('general-settings-memory-info');
  }

  override createInitialState() {
    return {
      memoryInfoData: null,
      free: 0,
      freeFormatted: '',
      total: 0,
      totalFormatted: '',
      swap: null,
      swapFormatted: null,
      items: [],
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, silent, cache, abortController }: FetchArgs) {
    if (silent === false) {
      this.set(this.createInitialState());
    }

    try {
      const time = silent === true ? 0 : 0;
      const waitPromise = wait(time);

      // Apply 30s timeout instead of 10s as this can take longer on busy Homeys
      // @ts-expect-errorerror TS2554: Expected 0 arguments, but got 1
      const memoryInfoPromise = api.system.getMemoryInfo({
        $timeout: 30000,
      }) as unknown as Promise<MemoryInfo>;

      const [, memoryInfoData] = await Promise.all([waitPromise, memoryInfoPromise]);

      if (abortController.signal.aborted) return;

      const free = memoryInfoData.free ?? 0;
      const total = memoryInfoData.total ?? 0;
      let swap = memoryInfoData.swap ?? null;

      if (swap === 0) {
        swap = null;
      }

      const items = Object.entries(memoryInfoData.types ?? {})
        .sort(([, a], [, b]) => b.size - a.size)
        .map(([key, value], index) => {
          return {
            ...value,
            key,
            sizeFormatted: formatBytes(value.size),
            color: getColorForIndex(index),
          };
        });

      this.set({
        ...this.createInitialState(),
        memoryInfoData: memoryInfoData,
        free,
        freeFormatted: formatBytes(free),
        total,
        totalFormatted: formatBytes(total),
        swap,
        swapFormatted: swap !== null ? formatBytes(swap) : null,
        items,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        isLoading: false,
        error,
      });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, 'general-settings-info-memory');

    return { generalSettingsInfoMemory: this.store(selectAll, shallow) };
  }
}

function selectAll(state: GeneralSettingsInfoMemoryStoreState) {
  return {
    ...selectBase(state),
    memoryInfoData: state.memoryInfoData,
    free: state.free,
    freeFormatted: state.freeFormatted,
    total: state.total,
    totalFormatted: state.totalFormatted,
    swap: state.swap,
    swapFormatted: state.swapFormatted,
    items: state.items,
  };
}

export const generalSettingsInfoMemoryStore = new GeneralSettingsInfoMemoryStore();
